import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Zap, AlertTriangle, ChevronLeft, ChevronRight, Smartphone, Truck, Package } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import contractLineService, {
  SuspensionLineDto,
  SuspensionStatsDto,
  PendingToSendLineDto,
  SuspensionsDashboardContractApi,
} from '../../services/contracts/ContractLineService';
import { contractService } from '../../services/contracts/ContractService';
import { canMarkFastchipEnviadoForStatus, getContractStatusText as getStatusText } from '../../utils/contractStatusUtils';
import { SuspensionStatsCards, ToSendStatsCards } from '../../components/Suspensions/stats-cards';
import { FiltersSection } from '../../components/Suspensions/filters-section';
import { ContractsTable } from '../../components/Suspensions/contracts-table';
import { CredentialsModal } from '../../components/Suspensions/credentials-modal';
import { LineSummaryModal } from '../../components/Suspensions/line-summary-modal';
import { AddressModal } from '../../components/Suspensions/address-modal';
import { ContractSummaryModal } from '../../components/Suspensions/contract-summary-modal';
import { FastLinkedContractModal } from '../../components/contracts/FastLinkedContractModal';
import { API_CONFIG } from '../../config/api.config';
import { UrgentActionsSection } from '../../components/Suspensions/urgent-actions-section';
import {
  SuspensionMarkDialog,
  type SuspensionMarkPayload,
} from '../../components/Suspensions/suspension-mark-dialog';
import {
  SuspensionTraceDialog,
  type SuspensionTracePayload,
} from '../../components/Suspensions/suspension-trace-dialog';
import { ImpedimentoListModal } from '../../components/Suspensions/impedimento-list-modal';
import { lineHasImpedimento } from '../../constants/suspensionOperationalReasons';
import type {
  GroupedContract,
  ToSendMetrics,
  ToSendStatsDto,
  PeriodFilter,
  StatusFilter,
  SuspensionViewFilter,
  ToSendStatusFilter,
  SuspensionsViewMode,
  ToSendAddressFilter,
  ToSendDataFilter,
  ToSendIccidFilter,
  ToSendShippingFilter,
  SuspensionPeriodMode,
} from '../../types/suspensions';
import {
  calculateToSendMetrics,
  filterToSendLines,
} from '../../utils/suspensionsToSendFilters';
import { extractApiErrorMessage, isCanceledError } from '../../services/utils/apiErrorUtils';
import { runSuspensionTraceBatch } from '../../utils/suspensionTraceBatch';

function mapDashboardToGroupedContracts(
  items: SuspensionsDashboardContractApi[],
  expandedContracts: Set<string>
): GroupedContract[] {
  return items.map((c) => ({
    contractId: String(c.contractId),
    contractNumber: c.contractNumber,
    clientName: c.clientName,
    clientCnpj: c.clientCnpj,
    expanded: expandedContracts.has(String(c.contractId)),
    lines: (c.lines ?? []).map((l) => ({
      id: String(l.id),
      type: l.type === 'toSend' ? 'toSend' : 'suspension',
      data: l.data as SuspensionLineDto | PendingToSendLineDto,
    })),
  }));
}

const SuspensionsPage: React.FC = () => {
  const { hasRole } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  
  const [suspensions, setSuspensions] = useState<SuspensionLineDto[]>([]);
  const [linesToSend, setLinesToSend] = useState<PendingToSendLineDto[]>([]);
  // viewMode substitui os antigos showSuspensions/showToSend, deixando explícito que
  // só uma visão (Fastchip / Por Entrega / A Enviar) está ativa por vez.
  const [viewMode, setViewMode] = useState<SuspensionsViewMode>('fastchip');
  const showSuspensions = viewMode !== 'tosend';
  const showToSend = viewMode === 'tosend';
  const contractFastchipParam: boolean | undefined =
    viewMode === 'fastchip' ? true : viewMode === 'entrega' ? false : undefined;
  const [expandedContracts, setExpandedContracts] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<SuspensionStatsDto | null>(null);
  const [toSendStats, setToSendStats] = useState<ToSendStatsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLineForCredentials, setSelectedLineForCredentials] = useState<string | null>(null);
  const [selectedClientIdForCredentials, setSelectedClientIdForCredentials] = useState<string | null>(null);
  const [selectedLineForSummary, setSelectedLineForSummary] = useState<SuspensionLineDto | null>(null);
  const [selectedLineForAddress, setSelectedLineForAddress] = useState<PendingToSendLineDto | null>(null);
  const [selectedContractForSummary, setSelectedContractForSummary] = useState<string | null>(null);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [contractSummaryModalOpen, setContractSummaryModalOpen] = useState(false);
  const [contractSummaryReloadKey, setContractSummaryReloadKey] = useState(0);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('esteMes');
  const [toSendPeriodFilter, setToSendPeriodFilter] = useState<PeriodFilter>('esteMes');
  /** RF11 / T14 — alinhado ao backend (`periodMode`); default pendências. */
  const [periodMode, setPeriodMode] = useState<SuspensionPeriodMode>('pendencias');

  const dateToInputValue = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const initToday = new Date();
  const [customDateFrom, setCustomDateFrom] = useState<string>(
    dateToInputValue(new Date(initToday.getFullYear(), initToday.getMonth(), 1))
  );
  const [customDateTo, setCustomDateTo] = useState<string>(dateToInputValue(initToday));
  const [toSendCustomDateFrom, setToSendCustomDateFrom] = useState<string>(
    dateToInputValue(new Date(initToday.getFullYear(), initToday.getMonth(), 1))
  );
  const [toSendCustomDateTo, setToSendCustomDateTo] = useState<string>(dateToInputValue(initToday));
  const [suspensionViewFilter, setSuspensionViewFilter] = useState<SuspensionViewFilter>('all');
  const [toSendStatusFilter, setToSendStatusFilter] = useState<ToSendStatusFilter>('all');
  const [toSendDataFilter, setToSendDataFilter] = useState<ToSendDataFilter>('all');
  const [toSendShippingFilter, setToSendShippingFilter] = useState<ToSendShippingFilter>('all');
  const [toSendContractStatusFilter, setToSendContractStatusFilter] = useState<string>('all');
  const [toSendIccidFilter, setToSendIccidFilter] = useState<ToSendIccidFilter>('all');
  const [toSendAddressFilter, setToSendAddressFilter] = useState<ToSendAddressFilter>('all');
  const [contractStatusFilter, setContractStatusFilter] = useState<string>('all');
  const [fastchipEnviadoFilter, setFastchipEnviadoFilter] = useState<string>('all');
  const [credenciaisFilter, setCredenciaisFilter] = useState<string>('all');
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(25);
  const [totalTableContracts, setTotalTableContracts] = useState(0);
  const [tablePageRawItems, setTablePageRawItems] = useState<SuspensionsDashboardContractApi[]>([]);
  const [priorityActionsExpanded, setPriorityActionsExpanded] = useState(false);
  const [metricsMode, setMetricsMode] = useState<'lines' | 'contracts'>('lines');
  const [reportLoading, setReportLoading] = useState(false);
  const [fastLinkedModalOpen, setFastLinkedModalOpen] = useState(false);
  const [fastLinkedContractId, setFastLinkedContractId] = useState<string | null>(null);
  const [fastLinkedHistoryCcId, setFastLinkedHistoryCcId] = useState<string | null>(null);
  const [fastLinkedReloadNonce, setFastLinkedReloadNonce] = useState(0);
  const [motivoFilter, setMotivoFilter] = useState<string[]>([]);
  const [markDialogOpen, setMarkDialogOpen] = useState(false);
  const [traceDialogOpen, setTraceDialogOpen] = useState(false);
  const [traceLines, setTraceLines] = useState<SuspensionLineDto[]>([]);
  const [impedimentoModalOpen, setImpedimentoModalOpen] = useState(false);
  const [pendingMarkLineIds, setPendingMarkLineIds] = useState<string[]>([]);
  const skipNextTablePageLoadRef = useRef(false);
  const hasMountedTablePageEffectRef = useRef(false);
  /** Debounce de filtros — evita rajadas de GET ao digitar/selecionar datas (~350ms). */
  const FILTER_DEBOUNCE_MS = 350;
  const filterDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTablePage = useCallback(() => {
    setTablePage((prev) => {
      if (prev !== 1) skipNextTablePageLoadRef.current = true;
      return 1;
    });
  }, []);

  const getPeriodDates = useCallback(() => {
    const today = new Date();

    switch (periodFilter) {
      case 'hoje': {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        return { dateFrom: start, dateTo: end };
      }
      case 'estaSemana': {
        // Semana calendário local segunda→domingo (ISO / prática BR). O fuso segue o do navegador;
        // para operação em SP, use o browser com timezone América/São_Paulo.
        const day = today.getDay(); // 0=dom … 6=sáb
        const daysSinceMonday = (day + 6) % 7;
        const start = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - daysSinceMonday,
          0,
          0,
          0,
          0
        );
        const end = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - daysSinceMonday + 6,
          23,
          59,
          59,
          999
        );
        return { dateFrom: start, dateTo: end };
      }
      case 'esteMes':
        return {
          dateFrom: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0),
          dateTo: today,
        };
      case 'ultimos30dias': {
        const start = new Date(today);
        start.setDate(today.getDate() - 30);
        return { dateFrom: start, dateTo: today };
      }
      case 'custom': {
        if (customDateFrom && customDateTo) {
          const from = new Date(customDateFrom + 'T00:00:00');
          const to = new Date(customDateTo + 'T23:59:59');
          return { dateFrom: from, dateTo: to };
        }
        return {
          dateFrom: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0),
          dateTo: today,
        };
      }
      default:
        return {
          dateFrom: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0),
          dateTo: today,
        };
    }
  }, [periodFilter, customDateFrom, customDateTo]);

  const loadStats = useCallback(async (signal?: AbortSignal) => {
    setLoadingStats(true);
    try {
      const { dateFrom, dateTo } = getPeriodDates();
      const data = await contractLineService.getSuspensionStats({ dateFrom, dateTo }, { signal });
      if (signal?.aborted) return;
      setStats(data);
    } catch (error: unknown) {
      if (isCanceledError(error) || signal?.aborted) return;
      console.error('Erro ao carregar estatísticas:', error);
      showError('Erro ao carregar estatísticas');
    } finally {
      if (!signal?.aborted) setLoadingStats(false);
    }
  }, [getPeriodDates, showError]);

  const loadToSendStats = useCallback(async (signal?: AbortSignal) => {
    try {
      const data = await contractLineService.getToSendStats({ signal });
      if (signal?.aborted) return;
      setToSendStats(data);
    } catch (error: unknown) {
      if (isCanceledError(error) || signal?.aborted) return;
      console.error('Erro ao carregar estatísticas de envio:', error);
      showError('Erro ao carregar estatísticas de envio');
    }
  }, [showError]);

  const getToSendPeriodDates = useCallback(() => {
    const today = new Date();

    switch (toSendPeriodFilter) {
      case 'hoje': {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        return { dateFrom: start, dateTo: end };
      }
      case 'estaSemana': {
        // Segunda 00:00 → domingo 23:59:59, calendário local (ver getPeriodDates).
        const day = today.getDay();
        const daysSinceMonday = (day + 6) % 7;
        const start = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - daysSinceMonday,
          0,
          0,
          0,
          0
        );
        const end = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - daysSinceMonday + 6,
          23,
          59,
          59,
          999
        );
        return { dateFrom: start, dateTo: end };
      }
      case 'esteMes':
        return {
          dateFrom: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0),
          dateTo: today,
        };
      case 'ultimos30dias': {
        const start = new Date(today);
        start.setDate(today.getDate() - 30);
        return { dateFrom: start, dateTo: today };
      }
      case 'custom': {
        if (toSendCustomDateFrom && toSendCustomDateTo) {
          const from = new Date(toSendCustomDateFrom + 'T00:00:00');
          const to = new Date(toSendCustomDateTo + 'T23:59:59');
          return { dateFrom: from, dateTo: to };
        }
        return {
          dateFrom: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0),
          dateTo: today,
        };
      }
      default:
        return {
          dateFrom: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0),
          dateTo: today,
        };
    }
  }, [toSendPeriodFilter, toSendCustomDateFrom, toSendCustomDateTo]);

  const loadDashboardLists = useCallback(async (signal?: AbortSignal) => {
    try {
      const { dateFrom, dateTo } = viewMode === 'tosend' ? getToSendPeriodDates() : getPeriodDates();
      const useFastchipOnlyFilters = viewMode === 'fastchip';
      const data = await contractLineService.getSuspensionsDashboardLists({
        searchTerm: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        contractStatus: contractStatusFilter !== 'all' ? contractStatusFilter : undefined,
        fastchipEnviado: useFastchipOnlyFilters && fastchipEnviadoFilter !== 'all' ? fastchipEnviadoFilter === 'true' : undefined,
        temCredenciaisMeuTim: useFastchipOnlyFilters && credenciaisFilter !== 'all' ? credenciaisFilter === 'true' : undefined,
        dateFrom,
        dateTo,
        periodMode,
        motivos: motivoFilter.length > 0 ? motivoFilter : undefined,
      }, { signal });
      if (signal?.aborted) return;
      setSuspensions(data.suspensions);
      setLinesToSend(data.linesToSend);
    } catch (error: unknown) {
      if (isCanceledError(error) || signal?.aborted) return;
      console.error('Erro ao carregar listas do dashboard:', error);
      showError('Erro ao carregar dados do dashboard');
    }
  }, [
    searchTerm,
    filterStatus,
    contractStatusFilter,
    fastchipEnviadoFilter,
    credenciaisFilter,
    viewMode,
    periodMode,
    motivoFilter,
    getPeriodDates,
    getToSendPeriodDates,
    showError,
  ]);

  const loadTablePage = useCallback(async (pageOverride?: number, signal?: AbortSignal) => {
    try {
      const { dateFrom, dateTo } = viewMode === 'tosend' ? getToSendPeriodDates() : getPeriodDates();
      const useFastchipOnlyFilters = viewMode === 'fastchip';
      const page = pageOverride ?? tablePage;
      const res = await contractLineService.getSuspensionsDashboardPage({
        page,
        pageSize: tablePageSize,
        showSuspensions,
        showToSend,
        suspensionViewFilter,
        searchTerm: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        contractStatus: contractStatusFilter !== 'all' ? contractStatusFilter : undefined,
        fastchipEnviado: useFastchipOnlyFilters && fastchipEnviadoFilter !== 'all' ? fastchipEnviadoFilter === 'true' : undefined,
        temCredenciaisMeuTim: useFastchipOnlyFilters && credenciaisFilter !== 'all' ? credenciaisFilter === 'true' : undefined,
        contractFastchip: contractFastchipParam,
        dateFrom,
        dateTo,
        periodMode,
        motivos: motivoFilter.length > 0 ? motivoFilter : undefined,
      }, { signal });
      if (signal?.aborted) return;
      setTotalTableContracts(res.totalContracts);
      setTablePageRawItems(res.items ?? []);
    } catch (error: unknown) {
      if (isCanceledError(error) || signal?.aborted) return;
      console.error('Erro ao carregar tabela paginada:', error);
      showError('Erro ao carregar lista de contratos');
    }
  }, [
    tablePage,
    tablePageSize,
    showSuspensions,
    showToSend,
    suspensionViewFilter,
    searchTerm,
    filterStatus,
    contractStatusFilter,
    fastchipEnviadoFilter,
    credenciaisFilter,
    contractFastchipParam,
    viewMode,
    periodMode,
    motivoFilter,
    getPeriodDates,
    getToSendPeriodDates,
    showError,
  ]);

  /** Recarrega dados do dashboard respeitando a aba ativa (evita GETs redundantes). */
  const refreshDashboardData = useCallback(
    async (options?: { tablePageOverride?: number; signal?: AbortSignal }) => {
      const signal = options?.signal;
      const tasks: Promise<unknown>[] = [loadDashboardLists(signal)];
      if (showSuspensions) {
        tasks.push(loadStats(signal));
        tasks.push(loadTablePage(options?.tablePageOverride ?? 1, signal));
      }
      if (showToSend) {
        tasks.push(loadToSendStats(signal));
      }
      await Promise.all(tasks);
    },
    [loadDashboardLists, loadStats, loadTablePage, loadToSendStats, showSuspensions, showToSend]
  );

  const toSendFilters = useMemo(
    () => ({
      dataFilter: toSendDataFilter,
      shippingFilter: toSendShippingFilter,
      contractStatusFilter: toSendContractStatusFilter,
      iccidFilter: toSendIccidFilter,
      addressFilter: toSendAddressFilter,
    }),
    [toSendAddressFilter, toSendContractStatusFilter, toSendDataFilter, toSendIccidFilter, toSendShippingFilter]
  );

  const filteredLinesToSend = useMemo(
    () => filterToSendLines(linesToSend, toSendFilters),
    [linesToSend, toSendFilters]
  );

  const toSendContractStatusOptions = useMemo(
    () => Array.from(new Set(linesToSend.map((line) => line.contractStatus).filter(Boolean))).sort(),
    [linesToSend]
  );

  const toSendTableItems = useMemo<SuspensionsDashboardContractApi[]>(() => {
    const grouped = filteredLinesToSend.reduce<Record<string, SuspensionsDashboardContractApi>>((acc, line) => {
      acc[line.contractId] = acc[line.contractId] || {
        contractId: line.contractId,
        contractNumber: line.contractNumber,
        clientName: line.clientName,
        clientCnpj: line.clientCnpj,
        lines: [],
      };
      acc[line.contractId].lines.push({ id: line.id, type: 'toSend', data: line });
      return acc;
    }, {});
    return Object.values(grouped);
  }, [filteredLinesToSend]);

  const pagedToSendTableItems = useMemo(() => {
    const start = (tablePage - 1) * tablePageSize;
    return toSendTableItems.slice(start, start + tablePageSize);
  }, [tablePage, tablePageSize, toSendTableItems]);

  const effectiveTableItems = showToSend ? pagedToSendTableItems : tablePageRawItems;
  const groupedTableContracts = useMemo(
    () => mapDashboardToGroupedContracts(effectiveTableItems, expandedContracts),
    [effectiveTableItems, expandedContracts]
  );

  const handleMarkAsSuspended = (lineId: string) => {
    setPendingMarkLineIds([lineId]);
    setMarkDialogOpen(true);
  };

  const resolveSuspensionLines = useCallback(
    async (lineIds: string[]): Promise<SuspensionLineDto[]> => {
      const uniqueIds = Array.from(new Set(lineIds));
      const resolved: SuspensionLineDto[] = [];
      const missing: string[] = [];

      for (const id of uniqueIds) {
        const fromList = suspensions.find((s) => s.id === id);
        if (fromList && !fromList.estaSuspensa) {
          resolved.push(fromList);
        } else {
          missing.push(id);
        }
      }

      if (missing.length > 0 && selectedContractForSummary) {
        try {
          const contractLines = await contractLineService.getPendingSuspensions({
            contractId: selectedContractForSummary,
          });
          for (const id of missing) {
            const line = contractLines.find((s) => s.id === id);
            if (line && !line.estaSuspensa) resolved.push(line);
          }
        } catch {
          /* ignora falha de fallback */
        }
      }

      return resolved;
    },
    [suspensions, selectedContractForSummary]
  );

  const handleEditImpedimento = async (lineId: string) => {
    const lines = await resolveSuspensionLines([lineId]);
    if (lines.length === 0) return;
    setTraceLines(lines);
    setTraceDialogOpen(true);
  };

  const handleEditImpedimentoBulk = async (lineIds: string[]) => {
    const lines = await resolveSuspensionLines(lineIds);
    if (lines.length === 0) {
      showError('Nenhuma linha pendente elegível para registar impedimento.');
      return;
    }
    setTraceLines(lines);
    setTraceDialogOpen(true);
  };

  const handleSaveSuspensionTrace = async (lineIds: string[], payload: SuspensionTracePayload) => {
    const body: Parameters<typeof contractLineService.updateSuspensionTrace>[1] = {};
    if (payload.clearMotivo) body.clearMotivo = true;
    else if (payload.motivoOperacional) body.motivoOperacional = payload.motivoOperacional;
    if (payload.clearDataPrevista) body.clearDataPrevista = true;
    else if (payload.dataPrevistaSuspensao) {
      body.dataPrevistaSuspensao = new Date(`${payload.dataPrevistaSuspensao}T12:00:00`).toISOString();
    }

    const result = await runSuspensionTraceBatch({
      lineIds,
      updateLine: (id) => contractLineService.updateSuspensionTrace(id, body),
      notifyError: showError,
      notifySuccess: showSuccess,
    });
    if (!result.ok) return result;

    await refreshDashboardData();
    if (contractSummaryModalOpen) {
      setContractSummaryReloadKey((k) => k + 1);
    }
    return result;
  };

  const executeMarkSuspended = async (lineIds: string[], payload: SuspensionMarkPayload) => {
    const suspensionDate =
      payload.mode === 'retroactive' && payload.suspensionDate
        ? new Date(`${payload.suspensionDate}T12:00:00`).toISOString()
        : undefined;

    const digits = payload.protocolo?.replace(/\D/g, "") ?? ""
    const result = await contractLineService.markAsSuspendedBatch({
      lineIds,
      suspensionDate,
      ...(digits.length >= 10
        ? lineIds.length === 1
          ? { protocolo: digits }
          : { protocolo: digits }
        : {}),
    });
    showSuccess(`${result.updated} linha(s) marcada(s) como suspensa(s).`);
    await refreshDashboardData();
  };

  const handleConfirmMarkSuspended = async (payload: SuspensionMarkPayload) => {
    try {
      await executeMarkSuspended(pendingMarkLineIds, payload);
    } catch (error: unknown) {
      console.error('Erro ao marcar como suspensa:', error);
      showError(extractApiErrorMessage(error, 'Erro ao marcar linha(s) como suspensa'));
      // Relança para o diálogo não fechar. O diálogo captura e não propaga ao Sentry.
      throw error;
    }
  };

  const handleMarkFastchipEnviado = async (contractId: string, contractStatusEnum?: string | null) => {
    if (!canMarkFastchipEnviadoForStatus(contractStatusEnum)) {
      window.alert(
        `Só é possível marcar o Fast Chip como enviado quando o status do contrato for Aprovado CQV, VTME Criado ou Ativo.\n\nStatus atual: ${getStatusText(contractStatusEnum)}.`
      );
      return;
    }
    if (!window.confirm('Deseja marcar o Fast Chip deste contrato como enviado?')) return;
    try {
      await contractService.markFastchipEnviado(contractId);
      showSuccess('Fast Chip marcado como enviado!');
      await refreshDashboardData();
    } catch (error: any) {
      showError(error.message || 'Erro ao marcar Fast Chip como enviado');
    }
  };

  const handleUnmarkSuspended = async (lineId: string) => {
    try {
      await contractLineService.unmarkAsSuspended(lineId);
      showSuccess('Suspensão revertida com sucesso!');
      await refreshDashboardData();
    } catch (error: any) {
      console.error('Erro ao reverter suspensão:', error);
      showError(error.response?.data?.message || 'Erro ao reverter suspensão');
    }
  };

  const scrollToElement = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      element.classList.add('ring-2', 'ring-offset-2', 'ring-blue-400');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-offset-2', 'ring-blue-400');
      }, 2000);
    }
  }, []);

  const handleScrollToSection = useCallback((sectionId: string) => {
    const visibleSuspensions =
      viewMode === 'fastchip' ? suspensions.filter((s) => s.fastchip) : viewMode === 'entrega' ? suspensions.filter((s) => !s.fastchip) : [];
    const firstMatchingLine = (() => {
      if (sectionId === 'first-suspension-section') {
        return visibleSuspensions.find((s) => s.dataSuspensao === null && s.eligibleForUrgencyMetrics);
      }
      if (sectionId === 'urgent-section') {
        return visibleSuspensions.find(
          (s) => s.diasRestantes !== null && s.diasRestantes <= 3 && s.eligibleForUrgencyMetrics
        );
      }
      if (sectionId === 'missing-credentials-section') {
        return visibleSuspensions.find((s) => s.fastchip && !s.temCredenciais);
      }
      if (sectionId === 'ready-to-ship-section' || (viewMode === 'tosend' && sectionId === 'contracts-table-section')) {
        return filteredLinesToSend[0];
      }
      return null;
    })();

    if (firstMatchingLine?.contractId) {
      setExpandedContracts((prev) => new Set(prev).add(firstMatchingLine.contractId));
      window.setTimeout(() => scrollToElement(`contract-card-${firstMatchingLine.contractId}`), 80);
      return;
    }

    scrollToElement(sectionId);
  }, [filteredLinesToSend, scrollToElement, suspensions, viewMode]);

  useLayoutEffect(() => {
    resetTablePage();
  }, [
    viewMode,
    filterStatus,
    contractStatusFilter,
    fastchipEnviadoFilter,
    credenciaisFilter,
    suspensionViewFilter,
    periodFilter,
    toSendPeriodFilter,
    customDateFrom,
    customDateTo,
    toSendCustomDateFrom,
    toSendCustomDateTo,
    toSendDataFilter,
    toSendShippingFilter,
    toSendContractStatusFilter,
    toSendIccidFilter,
    toSendAddressFilter,
    periodMode,
    motivoFilter,
    resetTablePage,
  ]);

  useEffect(() => {
    if (filterDebounceTimerRef.current) {
      clearTimeout(filterDebounceTimerRef.current);
    }

    let abortController: AbortController | null = null;
    filterDebounceTimerRef.current = setTimeout(() => {
      abortController = new AbortController();
      const signal = abortController.signal;
      const run = async () => {
        setLoading(true);
        try {
          await refreshDashboardData({ tablePageOverride: 1, signal });
        } finally {
          if (!signal.aborted) setLoading(false);
        }
      };
      void run();
    }, FILTER_DEBOUNCE_MS);

    return () => {
      abortController?.abort();
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
        filterDebounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    suspensionViewFilter,
    filterStatus,
    contractStatusFilter,
    fastchipEnviadoFilter,
    credenciaisFilter,
    periodFilter,
    toSendPeriodFilter,
    customDateFrom,
    customDateTo,
    toSendCustomDateFrom,
    toSendCustomDateTo,
    toSendDataFilter,
    toSendShippingFilter,
    toSendContractStatusFilter,
    toSendIccidFilter,
    toSendAddressFilter,
    periodMode,
    motivoFilter,
    refreshDashboardData,
  ]);

  useEffect(() => {
    if (!hasMountedTablePageEffectRef.current) {
      hasMountedTablePageEffectRef.current = true;
      return;
    }
    if (skipNextTablePageLoadRef.current) {
      skipNextTablePageLoadRef.current = false;
      return;
    }
    if (showToSend) return;

    const abortController = new AbortController();
    const run = async () => {
      setLoading(true);
      try {
        await loadTablePage(undefined, abortController.signal);
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    };
    void run();
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablePage, tablePageSize]);

  const toggleContractExpansion = useCallback((contractId: string) => {
    setExpandedContracts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contractId)) {
        newSet.delete(contractId);
      } else {
        newSet.add(contractId);
      }
      return newSet;
    });
  }, []);

  const hasActiveToSendClientFilter =
    toSendDataFilter !== 'all' ||
    toSendShippingFilter !== 'all' ||
    toSendContractStatusFilter !== 'all' ||
    toSendIccidFilter !== 'all' ||
    toSendAddressFilter !== 'all';

  // Calcular métricas de linhas a enviar. Quando há filtros client-side ativos, os
  // contadores usam a lista filtrada para não divergir da tabela.
  const toSendMetrics = useMemo((): ToSendMetrics => {
    if (hasActiveToSendClientFilter) {
      return calculateToSendMetrics(filteredLinesToSend);
    }

    const s = toSendStats;
    if (
      s &&
      s.pendingLinesTotal != null &&
      s.pendingContractsTotal != null &&
      s.pendingSedexContracts != null &&
      s.pendingPacContracts != null &&
      s.pendingLinesCompleteData != null &&
      s.pendingLinesIncompleteData != null
    ) {
      return {
        totalLines: s.pendingLinesTotal,
        totalContracts: s.pendingContractsTotal,
        sedexCount: s.pendingSedexContracts,
        pacCount: s.pendingPacContracts,
        completeData: s.pendingLinesCompleteData,
        incompleteData: s.pendingLinesIncompleteData,
      };
    }

    return calculateToSendMetrics(linesToSend);
  }, [filteredLinesToSend, hasActiveToSendClientFilter, linesToSend, toSendStats]);

  const effectiveTotalTableContracts = showToSend ? toSendTableItems.length : totalTableContracts;
  const totalTablePages = Math.max(1, Math.ceil(effectiveTotalTableContracts / tablePageSize) || 1);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await refreshDashboardData();
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = async () => {
    setTablePage(1);
    setLoading(true);
    try {
      await refreshDashboardData({ tablePageOverride: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handleViewContract = (contractId: string) => {
    navigate('/contracts', { state: { highlightContractId: contractId } });
  };

  const handleViewCredentials = (lineId: string, clientId: string) => {
    setSelectedLineForCredentials(lineId);
    setSelectedClientIdForCredentials(clientId);
    setCredentialsModalOpen(true);
  };

  const handleViewSummary = (lineId: string) => {
    const line = suspensions.find((s) => s.id === lineId);
    if (line) {
      setSelectedLineForSummary(line);
      setSummaryModalOpen(true);
    }
  };

  const handleViewAddress = (line: PendingToSendLineDto) => {
    setSelectedLineForAddress(line);
    setAddressModalOpen(true);
  };

  const handleViewContractSummary = (contractId: string) => {
    setSelectedContractForSummary(contractId);
    setContractSummaryModalOpen(true);
  };

  const handleOpenFastLinkedContract = (contractId: string, chipCcidForHistory: string | null) => {
    setFastLinkedContractId(contractId);
    setFastLinkedHistoryCcId(chipCcidForHistory);
    setFastLinkedModalOpen(true);
  };

  const closeFastLinkedContractModal = () => {
    setFastLinkedModalOpen(false);
    setFastLinkedContractId(null);
    setFastLinkedHistoryCcId(null);
  };

  const handleExportExcel = async () => {
    try {
      const { dateFrom, dateTo } = getToSendPeriodDates();
      const { dateFrom: suspensionDateFrom, dateTo: suspensionDateTo } = getPeriodDates();
      const useFastchipOnlyFilters = viewMode === 'fastchip';

      const params = new URLSearchParams();
      if (searchTerm) params.append('searchTerm', searchTerm);
      if (showToSend) {
        params.append('toSendDateFrom', dateFrom.toISOString());
        params.append('toSendDateTo', dateTo.toISOString());
      }
      if (showSuspensions) {
        params.append('suspensionDateFrom', suspensionDateFrom.toISOString());
        params.append('suspensionDateTo', suspensionDateTo.toISOString());
      }
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (contractStatusFilter !== 'all') params.append('contractStatus', contractStatusFilter);
      if (useFastchipOnlyFilters && fastchipEnviadoFilter !== 'all') params.append('fastchipEnviado', fastchipEnviadoFilter);
      if (useFastchipOnlyFilters && credenciaisFilter !== 'all') params.append('temCredenciaisMeuTim', credenciaisFilter);
      // Sem esses parâmetros o backend ignorava o filtro do tipo (Fastchip vs Por Entrega)
      // e o filtro de Pendentes/Já suspensas, exportando todas as linhas.
      if (contractFastchipParam !== undefined) {
        params.append('contractFastchip', String(contractFastchipParam));
      }
      if (suspensionViewFilter && suspensionViewFilter !== 'all') {
        params.append('suspensionViewFilter', suspensionViewFilter);
      }
      if (showSuspensions && periodMode) {
        params.append('periodMode', periodMode);
      }
      params.append('showSuspensions', showSuspensions.toString());
      params.append('showToSend', showToSend.toString());
      motivoFilter.forEach((m) => params.append('motivos', m));

      const url = `${API_CONFIG.baseURL}/v1/contract-lines/export-excel?${params.toString()}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Erro ao exportar');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `linhas-${showSuspensions ? 'suspender' : ''}${showSuspensions && showToSend ? '-' : ''}${showToSend ? 'enviar' : ''}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      showSuccess('Relatório exportado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao exportar:', error);
      showError('Erro ao exportar relatório');
    }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const { dateFrom, dateTo } = viewMode === 'tosend' ? getToSendPeriodDates() : getPeriodDates();
      await contractLineService.generateSuspensionsReport({
        dateFrom,
        dateTo,
        viewMode,
        periodMode: viewMode === 'tosend' ? undefined : periodMode,
        sendEmail: true,
        sendWhatsApp: false,
      });
      showSuccess('Relatório gerado e enviado por e-mail!');
    } catch {
      showError('Erro ao gerar relatório');
    } finally {
      setReportLoading(false);
    }
  };

  const tabSubtitle = useMemo(() => {
    if (viewMode === 'fastchip')
      return 'Linhas Fastchip — suspensão feita pela equipe interna no Meu TIM (renovar a cada 120 dias).';
    if (viewMode === 'entrega')
      return 'Linhas Por Entrega — consultores são responsáveis pelo processo de suspensão após receberem o chip.';
    return 'Linhas Fastchip aguardando ICCID e endereço para envio (3+ linhas = SEDEX, 1–2 linhas = PAC).';
  }, [viewMode]);

  // Contagens por tab calculadas in-memory a partir das listas completas devolvidas pelo
  // /lists. Como o backend não filtra por contractFastchip nessa chamada, aqui fica fácil
  // mostrar o total de cada categoria independente de qual tab está ativa.
  const tabCounts = useMemo(() => {
    const fastchipCount = suspensions.filter((s) => s.fastchip).length;
    const entregaCount = suspensions.filter((s) => !s.fastchip).length;
    return {
      fastchip: fastchipCount,
      entrega: entregaCount,
      tosend: linesToSend.length,
    };
  }, [suspensions, linesToSend]);

  // Stats client-side por viewMode — o endpoint global de stats não conhece o filtro de
  // tipo, então recalculamos a partir das suspensões já em memória para que cada aba
  // exiba números consistentes com sua tab.
  const suspensionsForViewMode = useMemo(() => {
    if (viewMode === 'fastchip') return suspensions.filter((s) => s.fastchip);
    if (viewMode === 'entrega') return suspensions.filter((s) => !s.fastchip);
    return [];
  }, [viewMode, suspensions]);

  const viewModeStats = useMemo<SuspensionStatsDto | null>(() => {
    if (viewMode === 'tosend' || !stats) return stats;
    const list = suspensionsForViewMode;
    let urgent = 0;
    let alert = 0;
    let normal = 0;
    let firstSuspension = 0;
    let missingCredentials = 0;
    let withImpedimento = 0;
    list.forEach((l) => {
      if (lineHasImpedimento(l)) {
        withImpedimento += 1;
        return;
      }
      if (l.dataSuspensao === null && l.eligibleForUrgencyMetrics) firstSuspension += 1;
      const d = l.diasRestantes;
      if (d != null) {
        if (d <= 3) urgent += 1;
        else if (d <= 7) alert += 1;
        else normal += 1;
      }
      if (l.fastchip && !l.temCredenciais) missingCredentials += 1;
    });
    return {
      ...stats,
      urgent,
      alert,
      normal,
      firstSuspension,
      missingCredentials,
      withImpedimento,
      totalPending: list.length,
    };
  }, [viewMode, suspensionsForViewMode, stats]);

  const impactedSuspensionContracts = useMemo(() => {
    return new Set(suspensionsForViewMode.map((s) => s.contractId)).size;
  }, [suspensionsForViewMode]);

  const firstSuspensionCount = useMemo(() => {
    return suspensionsForViewMode.filter(
      (s) => !lineHasImpedimento(s) && s.dataSuspensao === null && s.eligibleForUrgencyMetrics
    ).length;
  }, [suspensionsForViewMode]);

  const hasCriticalPriorityActions = useMemo(
    () =>
      suspensionsForViewMode.some(
        (s) =>
          !lineHasImpedimento(s) &&
          ((s.dataSuspensao === null && s.eligibleForUrgencyMetrics) ||
            (s.diasRestantes !== null && s.diasRestantes <= 3 && s.eligibleForUrgencyMetrics))
      ),
    [suspensionsForViewMode]
  );

  useEffect(() => {
    if (hasCriticalPriorityActions) setPriorityActionsExpanded(true);
  }, [hasCriticalPriorityActions]);

  // Verificar se o usuário tem permissão Admin
  if (!hasRole('Admin')) {
    return (
      <GlassPageWrapper title="Dashboard de Suspensões" icon="pause circle">
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            Acesso negado. Apenas administradores podem visualizar esta página.
          </AlertDescription>
        </Alert>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Dashboard de Suspensões" icon="pause circle">
      {/* Loading Overlay */}
      {loading && !stats && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
            <p className="text-muted-foreground">Carregando dados...</p>
          </div>
        </div>
      )}

      {/* Filtros: logo abaixo do título para facilitar o uso antes de navegar pelas tabs */}
      <section className="mb-6">
        <FiltersSection
          viewMode={viewMode}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={handleSearchSubmit}
          filterStatus={filterStatus}
          onFilterStatusChange={setFilterStatus}
          suspensionViewFilter={suspensionViewFilter}
          onSuspensionViewFilterChange={setSuspensionViewFilter}
          toSendStatusFilter={toSendStatusFilter}
          onToSendStatusFilterChange={setToSendStatusFilter}
          toSendDataFilter={toSendDataFilter}
          onToSendDataFilterChange={setToSendDataFilter}
          toSendShippingFilter={toSendShippingFilter}
          onToSendShippingFilterChange={setToSendShippingFilter}
          toSendContractStatusFilter={toSendContractStatusFilter}
          onToSendContractStatusFilterChange={setToSendContractStatusFilter}
          toSendIccidFilter={toSendIccidFilter}
          onToSendIccidFilterChange={setToSendIccidFilter}
          toSendAddressFilter={toSendAddressFilter}
          onToSendAddressFilterChange={setToSendAddressFilter}
          toSendContractStatusOptions={toSendContractStatusOptions}
          periodFilter={periodFilter}
          onPeriodFilterChange={setPeriodFilter}
          customDateFrom={customDateFrom}
          customDateTo={customDateTo}
          onCustomDateFromChange={setCustomDateFrom}
          onCustomDateToChange={setCustomDateTo}
          toSendPeriodFilter={toSendPeriodFilter}
          onToSendPeriodFilterChange={setToSendPeriodFilter}
          toSendCustomDateFrom={toSendCustomDateFrom}
          toSendCustomDateTo={toSendCustomDateTo}
          onToSendCustomDateFromChange={setToSendCustomDateFrom}
          onToSendCustomDateToChange={setToSendCustomDateTo}
          contractStatusFilter={contractStatusFilter}
          onContractStatusFilterChange={setContractStatusFilter}
          fastchipEnviadoFilter={fastchipEnviadoFilter}
          onFastchipEnviadoFilterChange={setFastchipEnviadoFilter}
          credenciaisFilter={credenciaisFilter}
          onCredenciaisFilterChange={setCredenciaisFilter}
          periodMode={periodMode}
          onPeriodModeChange={setPeriodMode}
          motivoFilter={motivoFilter}
          onMotivoFilterChange={(values) => {
            setMotivoFilter(values);
            resetTablePage();
          }}
          onRefresh={handleRefresh}
          onExportExcel={handleExportExcel}
          onGenerateReport={handleGenerateReport}
          loading={loading || loadingStats || reportLoading}
        />
      </section>

      {/* Tabs: Fastchip / Por Entrega / A Enviar */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2 border-b border-border">
          {(
            [
              { id: 'fastchip' as const, label: 'Fastchip (Meu TIM)', icon: Smartphone, count: tabCounts.fastchip, color: 'blue' },
              { id: 'entrega' as const, label: 'Por Entrega', icon: Truck, count: tabCounts.entrega, color: 'orange' },
              { id: 'tosend' as const, label: 'A Enviar', icon: Package, count: tabCounts.tosend, color: 'violet' },
            ]
          ).map((tab) => {
            const isActive = viewMode === tab.id;
            const Icon = tab.icon;
            const activeBorder =
              tab.color === 'blue'
                ? 'border-blue-600 text-blue-700'
                : tab.color === 'orange'
                ? 'border-orange-600 text-orange-700'
                : 'border-violet-600 text-violet-700';
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setViewMode(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? activeBorder
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                <span
                  className={`ml-1 inline-flex items-center justify-center rounded-full px-2 text-xs font-semibold ${
                    isActive ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-sm text-muted-foreground">{tabSubtitle}</p>
        {(() => {
          const { dateFrom, dateTo } =
            viewMode === 'tosend' ? getToSendPeriodDates() : getPeriodDates();
          const fmt = (d: Date) =>
            d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          return (
            <p className="mt-1 text-sm text-muted-foreground">
              Exibindo: {fmt(dateFrom)} – {fmt(dateTo)}
            </p>
          );
        })()}
      </div>

      {/* First Suspension highlight — só faz sentido na aba Fastchip */}
      {viewMode === 'fastchip' && firstSuspensionCount > 0 && (
        <div className="mb-6">
          <button
            onClick={() => handleScrollToSection('first-suspension-section')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-fuchsia-100 border border-fuchsia-300 rounded-lg animate-pulse hover:bg-fuchsia-200 transition-colors cursor-pointer"
          >
            <Zap className="h-5 w-5 text-fuchsia-600" />
            <span className="font-semibold text-fuchsia-700">
              {firstSuspensionCount} linha{firstSuspensionCount !== 1 ? 's' : ''} aguardando 1ª SUSPENSÃO - PRIORIDADE MÁXIMA
            </span>
          </button>
        </div>
      )}

      {/* Stats Cards - Suspensions FIRST */}
      {showSuspensions && viewModeStats && (
        <section className="mb-6">
          {/* Toggle Linhas / Contratos */}
          <div className="flex items-center gap-1 mb-3">
            <span className="text-xs text-muted-foreground mr-1">Visualizar por:</span>
            <button
              type="button"
              onClick={() => setMetricsMode('lines')}
              className={`px-3 py-1 text-xs rounded-l border font-medium transition-colors ${
                metricsMode === 'lines'
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-background text-foreground border-input hover:bg-muted'
              }`}
            >
              Linhas
            </button>
            <button
              type="button"
              onClick={() => setMetricsMode('contracts')}
              className={`px-3 py-1 text-xs rounded-r border-t border-b border-r font-medium transition-colors ${
                metricsMode === 'contracts'
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-background text-foreground border-input hover:bg-muted'
              }`}
            >
              Contratos
            </button>
          </div>
          <SuspensionStatsCards
            stats={viewModeStats}
            viewMode={viewMode}
            impactedContracts={impactedSuspensionContracts}
            onCardClick={handleScrollToSection}
            onImpedimentoCardClick={() => setImpedimentoModalOpen(true)}
            metricsMode={metricsMode}
            suspensionLines={suspensionsForViewMode}
          />
        </section>
      )}

      {/* Stats Cards - To Send */}
      {showToSend &&
        (linesToSend.length > 0 || (toSendStats?.pendingLinesTotal ?? 0) > 0 || toSendMetrics.totalLines > 0) && (
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Métricas de Linhas a Enviar</h2>
          <ToSendStatsCards metrics={toSendMetrics} stats={toSendStats} onCardClick={handleScrollToSection} />
        </section>
      )}

      {/* Urgent Actions - AFTER metrics and charts */}
      {viewMode !== 'tosend' && (
        <UrgentActionsSection
          viewMode={viewMode}
          suspensions={suspensionsForViewMode}
          linesToSend={linesToSend}
          onViewLine={handleViewSummary}
          onViewContract={handleViewContract}
          onViewContractSummary={handleViewContractSummary}
          expanded={priorityActionsExpanded}
          onExpandedChange={setPriorityActionsExpanded}
        />
      )}

      {/* Contracts Table */}
      <section className="mb-6" id="contracts-table-section">
        <ContractsTable
          contracts={groupedTableContracts}
          showSuspensions={showSuspensions}
          showToSend={showToSend}
          expandedContracts={expandedContracts}
          onToggleExpand={toggleContractExpansion}
          onViewContract={handleViewContract}
          onViewContractSummary={handleViewContractSummary}
          onViewCredentials={handleViewCredentials}
          onMarkSuspended={handleMarkAsSuspended}
          onEditImpedimento={handleEditImpedimento}
          onMarkFastchipEnviado={handleMarkFastchipEnviado}
          onViewSummary={handleViewSummary}
          onViewAddress={handleViewAddress}
          isAdmin={hasRole('Admin')}
          onOpenFastLinkedContract={handleOpenFastLinkedContract}
        />
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t pt-4">
          <p className="text-sm text-muted-foreground">
            {effectiveTotalTableContracts === 0
              ? 'Nenhum contrato nesta página'
              : `Página ${tablePage} de ${totalTablePages} — ${effectiveTotalTableContracts} contrato(s) no total`}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">Contratos por página</span>
            <Select
              value={String(tablePageSize)}
              onValueChange={(v) => {
                setTablePageSize(Number(v));
                setTablePage(1);
              }}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100, 200].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={tablePage <= 1 || loading}
                onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={tablePage >= totalTablePages || loading}
                onClick={() => setTablePage((p) => Math.min(totalTablePages, p + 1))}
                aria-label="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <CredentialsModal
        lineId={selectedLineForCredentials}
        clientIdForCredentials={selectedClientIdForCredentials}
        open={credentialsModalOpen}
        onClose={() => {
          setCredentialsModalOpen(false);
          setSelectedLineForCredentials(null);
          setSelectedClientIdForCredentials(null);
        }}
        onCredentialsUpdated={() => {
          void refreshDashboardData();
        }}
      />

      <LineSummaryModal
        line={selectedLineForSummary}
        open={summaryModalOpen}
        onClose={() => {
          setSummaryModalOpen(false);
          setSelectedLineForSummary(null);
        }}
        onSuspend={handleMarkAsSuspended}
      />

      <AddressModal
        line={selectedLineForAddress}
        open={addressModalOpen}
        onClose={() => {
          setAddressModalOpen(false);
          setSelectedLineForAddress(null);
        }}
      />

      <ContractSummaryModal
        contractId={selectedContractForSummary}
        open={contractSummaryModalOpen}
        onClose={() => {
          setContractSummaryModalOpen(false);
          setSelectedContractForSummary(null);
        }}
        onCredentialsUpdated={() => {
          void refreshDashboardData();
        }}
        onMarkSuspended={handleMarkAsSuspended}
        onEditImpedimento={handleEditImpedimento}
        onEditImpedimentoBulk={handleEditImpedimentoBulk}
        onRequestBatchSuspend={(lineIds) => {
          setPendingMarkLineIds(lineIds);
          setMarkDialogOpen(true);
        }}
        onUnmarkSuspended={handleUnmarkSuspended}
        onBatchUpdated={() => {
          void handleRefresh();
        }}
        reloadKey={contractSummaryReloadKey}
      />

      <SuspensionMarkDialog
        open={markDialogOpen}
        lineCount={pendingMarkLineIds.length}
        onClose={() => {
          setMarkDialogOpen(false);
          setPendingMarkLineIds([]);
        }}
        onConfirm={handleConfirmMarkSuspended}
      />

      <SuspensionTraceDialog
        open={traceDialogOpen}
        lines={traceLines}
        onClose={() => {
          setTraceDialogOpen(false);
          setTraceLines([]);
        }}
        onSave={handleSaveSuspensionTrace}
      />

      <ImpedimentoListModal
        open={impedimentoModalOpen}
        suspensions={suspensionsForViewMode}
        expandedContracts={expandedContracts}
        onToggleExpand={toggleContractExpansion}
        onClose={() => setImpedimentoModalOpen(false)}
        onViewContract={handleViewContract}
        onViewContractSummary={handleViewContractSummary}
        onViewCredentials={handleViewCredentials}
        onMarkSuspended={handleMarkAsSuspended}
        onEditImpedimento={handleEditImpedimento}
        onViewSummary={handleViewSummary}
        isAdmin={hasRole('Admin')}
        metricsMode={metricsMode}
      />

      <FastLinkedContractModal
        open={fastLinkedModalOpen}
        onClose={closeFastLinkedContractModal}
        focusContractId={fastLinkedContractId}
        isAdmin={hasRole('Admin')}
        fastchipBatchBypassMovementSource="SuspensionsModal"
        fallbackHistoryChipCcId={fastLinkedHistoryCcId}
        reloadNonce={fastLinkedReloadNonce}
        onAfterSwapSuccess={() => {
          setFastLinkedReloadNonce((n) => n + 1);
          void handleRefresh();
        }}
      />
    </GlassPageWrapper>
  );
};

export default SuspensionsPage;
